import { Box, Button } from '@coolblue-development/becky';
import { Loader } from '@coolblue-development/freddy';
import { datadogLogs } from '@datadog/browser-logs';
import { useState } from 'react';
import { EmailMessage } from '../../../../models/Email';
import { useEmailContext } from '../../../../contexts/EmailContext';
import { fetchEmailContent } from '../../../../services/fetchEmailContent';
import { useEnrichEmailWithContent } from '../../hooks/useEnrichEmailWithContent';
import { PREVIOUS_EMAILS_BATCH_SIZE } from '../../../../constants/email';
import EmailMessageItem from './Body/EmailMessageItem';

interface PreviousEmailsProps {
  messages: EmailMessage[];
  onAttachmentDownloadError?: () => void;
}

const PreviousEmails = ({ messages, onAttachmentDownloadError }: PreviousEmailsProps): JSX.Element | null => {
  const { selectedEmail, updateMessageContent } = useEmailContext();
  const enrichEmailWithContent = useEnrichEmailWithContent();
  const [isLoadingBatch, setIsLoadingBatch] = useState(false);

  if (messages.length === 0) return null;

  const unloaded = [...messages].reverse().filter(m => !m.contentLoaded);
  const nextBatchSize = Math.min(unloaded.length, PREVIOUS_EMAILS_BATCH_SIZE);

  const loadNextBatch = async (): Promise<void> => {
    if (!selectedEmail || unloaded.length === 0 || isLoadingBatch) return;

    const batch = unloaded.slice(0, PREVIOUS_EMAILS_BATCH_SIZE);

    // `remaining` is what tells us whether PREVIOUS_EMAILS_BATCH_SIZE is too small: a non-zero
    // value means the agent has to click again to see the rest of the thread.
    datadogLogs.logger.info('Load previous emails clicked', {
      previousEmailsLoad: true,
      batchSize: batch.length,
      remaining: unloaded.length - batch.length
    });

    setIsLoadingBatch(true);
    try {
      const loaded = await Promise.all(
        batch.map(async message => {
          try {
            return { message, content: await fetchEmailContent(message.contactId) };
          } catch (error) {
            console.error('Failed to load previous email content:', error);
            return null;
          }
        })
      );

      loaded.forEach(result => {
        if (!result) return;
        updateMessageContent(
          selectedEmail,
          result.message.contactId,
          enrichEmailWithContent(result.message, result.content)
        );
      });
    } finally {
      setIsLoadingBatch(false);
    }
  };

  return (
    <Box sx={{ marginTop: '16px' }}>
      {isLoadingBatch && <Loader />}
      {!isLoadingBatch && unloaded.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
          <Button preset="text" size="small" onClick={loadNextBatch}>
            Load {nextBatchSize} previous email{nextBatchSize > 1 ? 's' : ''}
          </Button>
        </Box>
      )}
      {messages
        .map((message, index) => ({ message, index }))
        .filter(({ message }) => message.contentLoaded)
        .map(({ message, index }) => (
          <EmailMessageItem
            key={message.contactId}
            message={message}
            index={index}
            onAttachmentDownloadError={onAttachmentDownloadError}
          />
        ))}
    </Box>
  );
};

export default PreviousEmails;
